import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { SecurityClient } from "app/dal/generated/clients";
import { lastValueFrom, ReplaySubject } from "rxjs";
import { map } from "rxjs/operators";
import { User } from "./user";
import { USER_OBSERVERS } from "./user-observer";

const tokenKey = "band-manager:bearer-token";

@Injectable({
    providedIn: "root",
})
export class AuthenticationService {
    private securityClient = inject(SecurityClient);
    private userObservers = inject(USER_OBSERVERS);

    public readonly user$ = new ReplaySubject<User | undefined>(1);
    public readonly isLoggedIn$ = this.user$.pipe(map((user) => !!user));

    public get bearerToken() {
        return sessionStorage.getItem(tokenKey);
    }

    public async login(username: string, password: string) {
        const user = await this.performLogin(username, password);
        this.processLoggedInUser(user);
        return user;
    }

    private async performLogin(username: string, password: string): Promise<User> {
        try {
            const response = await lastValueFrom(
                this.securityClient.login({ userName: username, password }),
            );
            sessionStorage.setItem(tokenKey, response.token);
            return new User(response.user);
        } catch (errorResponse) {
            if (errorResponse instanceof HttpErrorResponse && errorResponse.status === 401) {
                return Promise.reject(new Error("Incorrect username or password"));
            } else {
                return Promise.reject(new Error("Could not connect to server"));
            }
        }
    }

    private processLoggedInUser(user: User | undefined) {
        this.user$.next(user);
        this.userObservers.forEach((o) => o.onUserChanged(user));

        return user;
    }

    public async checkForCurrentSession(): Promise<User | undefined> {
        try {
            if (!this.bearerToken) {
                this.processLoggedInUser(undefined);
                return;
            }

            const response = await lastValueFrom(this.securityClient.getLoggedInUser());
            return this.processLoggedInUser(new User(response));
        } catch (e) {
            this.processLoggedInUser(undefined);
        }
    }

    public async logout() {
        try {
            await lastValueFrom(this.securityClient.logout());
            sessionStorage.removeItem(tokenKey);
        } finally {
            this.processLoggedInUser(undefined);
        }
    }

    public requestPasswordReset(email: string, redirect?: string) {
        return lastValueFrom(this.securityClient.requestResetPassword({ email, redirect }));
    }

    public resetPassword(email: string, token: string, password: string) {
        return lastValueFrom(this.securityClient.resetPassword({ email, password, token }));
    }
}
